import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
    Box,
    FormControl,
    Typography,
    Chip,
    Divider,
    CircularProgress,
    FormHelperText,
    Container,
    Card,
    CardContent
} from "@mui/material";
import Question from "../../components/Question";
import { MinorUserContext } from "../../context/UserNameProvider";
import { setBooked as updateBooked, setSelectedSlot } from "../../redux/userSlice";
import { callBackendApiGet, alertOps } from "../../lib/api";
import logDrtEvent from "../../lib/logging";
import { cardStyle } from "../../lib/uiformatting";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export default function AppointmentSlots() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { firstName, isMinor } = useContext(MinorUserContext);

    const [visibleCounts, setVisibleCounts] = useState({});
    const [availability, setAvailability] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const location = useLocation();
    const { portal = false, expectationData } = location.state || {};
    const storedPatient = useSelector((state) => state.user.selectedAppointmentPatient);
    const patient = expectationData || storedPatient;

    const fetchedOnceRef = useRef(false);

    const user = useSelector((state) => state.user);

    useEffect(() => {
        if (!patient) {
            setErrorMessage("Patient information is missing. Please return to the previous page.");
            setLoading(false);
        }
    }, [patient]);

    useEffect(() => {
        async function fetchSlots() {
            if (!patient?.state || !patient?.insurance?.payerName) {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const response = await callBackendApiGet(
                    `/api/v1/appointments-slots?state=${encodeURIComponent(
                        patient.state
                    )}&payer=${encodeURIComponent(
                        patient.insurance.payerName
                    )}&patientID=${patient.id}`
                );

                const slotsByDate = await response.json();

                const byLocalDate = {};

                Object.values(slotsByDate).forEach((slots) => {
                    slots.forEach((slot) => {
                        const local = dayjs.utc(slot.startTimeInMS).local();

                        const localDateKey = local.format("YYYY-MM-DD");

                        if (!byLocalDate[localDateKey]) {
                            byLocalDate[localDateKey] = [];
                        }

                        byLocalDate[localDateKey].push({
                            ...slot,
                            date: local.format("dddd, MMM D"),
                            time: local.format("hh:mm A"),
                            datetime: local.toISOString(),
                            clinicianID: slot.userId,
                            localMs: local.valueOf(),
                        });
                    });
                });

                const grouped = Object.entries(byLocalDate)
                    .sort(
                        ([dateA], [dateB]) =>
                            dayjs(dateA).valueOf() - dayjs(dateB).valueOf()
                    )
                    .map(([dateKey, slots]) => ({
                        date: dayjs(dateKey).format("dddd, MMM D"),
                        slots: slots.sort((a, b) => a.localMs - b.localMs),
                    }))
                    .filter((day) => day.slots.length > 0);
                setAvailability(grouped);
            } catch (err) {
                await alertOps(user, "Appointment slots", err, "fetchSlots");
                logDrtEvent(`Failed to fetch appointment slots: ${err}`);
                setErrorMessage("Something went wrong. Please try again later.");
            } finally {
                setLoading(false);
            }
        }

        if (patient && !fetchedOnceRef.current) {
            fetchedOnceRef.current = true;
            fetchSlots();
        }
    }, [patient]);

    const handleShowMore = (date) => {
        setVisibleCounts((prev) => ({
            ...prev,
            [date]: (prev[date] || 9) + 3,
        }));
    };

    const handleSlotSelection = (slot) => {
        dispatch(updateBooked(true));
        dispatch(setSelectedSlot(slot));
        navigate("/appointment-confirm", { state: { portal } });
    };

    return (
        <Question
            title={`Please choose a time for ${isMinor ? `${firstName}'s` : "your"} follow-up visit.`}
            subtitle="All times are shown in your local time zone."
            input={
                loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                        <CircularProgress sx={{ color: 'piction.highlight' }} />
                    </Box>
                ) : availability.length === 0 ? (
                    <Container maxWidth="sm" sx={{ mt: 4 }}>
                        <Card elevation={0} sx={{ ...cardStyle, textAlign: "center", p: 0 }}>
                            <CardContent>
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    There are no appointment times available right now.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Container>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <FormControl sx={{ width: "100%", mb: "2rem" }} required>
                            {availability.map((day, i) => {
                                const visibleSlotCount = visibleCounts[day.date] || 9;
                                const slotsToShow = day.slots.slice(0, visibleSlotCount);
                                const hasMore = visibleSlotCount < day.slots.length;

                                return (
                                    <Box key={day.date} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
                                            {day.date}
                                        </Typography>

                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(3, 1fr)",
                                                gap: 1.5,
                                            }}
                                        >
                                            {slotsToShow.map((slot) => (
                                                <Chip
                                                    key={`${slot.startTimeInMS}`}
                                                    label={slot.time}
                                                    onClick={() => handleSlotSelection(slot)}
                                                    variant="outlined"
                                                    sx={{
                                                        fontFamily: "Inter, sans-serif",
                                                        fontWeight: 500,
                                                        fontSize: "clamp(10px, 2.5vh, 14px)",
                                                        borderColor: "grayTones.piction",
                                                        borderStyle: "solid",
                                                        borderWidth: "1.px",
                                                        borderRadius: "25px",
                                                        px: 1,
                                                        py: 1,
                                                        justifySelf: "center",
                                                        transition: "all 0.3s ease",
                                                        color: "black",
                                                        backgroundColor: "white",
                                                        "&:hover": {
                                                            backgroundColor: "rgba(34, 0, 187, 0.04)",
                                                            borderColor: "piction.highlight",
                                                            color: "piction.highlight",
                                                        },
                                                    }}
                                                />
                                            ))}

                                            {hasMore && (
                                                <Box sx={{ gridColumn: "span 3", textAlign: "center", mt: 1 }}>
                                                    <Chip
                                                        label="Show More"
                                                        onClick={() => handleShowMore(day.date)}
                                                        sx={{
                                                            borderRadius: "999px",
                                                            px: 3,
                                                            py: 1.5,
                                                            fontWeight: 500,
                                                            fontFamily: "Inter, sans-serif",
                                                            width: "100%",
                                                            color: "black",
                                                            backgroundColor: "white",
                                                            borderColor: "grayTones.piction",
                                                            "&:hover": {
                                                                backgroundColor: "rgba(34, 0, 187, 0.04)",
                                                                borderColor: "piction.highlight",
                                                                color: "piction.highlight",
                                                            },
                                                        }}
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            )}
                                        </Box>

                                        {i < availability.length - 1 && (
                                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", mt: 3 }}>
                                                <Box sx={{ gridColumn: "span 3" }}>
                                                    <Divider />
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </FormControl>

                        {errorMessage && (
                            <FormHelperText error sx={{ fontSize: "0.85rem", mt: 1, textAlign: "center" }}>
                                {errorMessage}
                            </FormHelperText>
                        )}
                    </Box>
                )
            }
        />
    );
}
